/**
 * The "when" of a category page: the booking bar's day, time and guests, and
 * the "Open now" / "Tables tonight" chips. All of it lives in the URL, so
 * Back, refresh, a shared link and the language switch keep it.
 *
 * Search params (the detail page's booking sheet reads the same three names,
 * see `selectionFromSearch` in @majourneys/booking and MaJourneys AI's
 * `proposalHref`):
 *   date=YYYY-MM-DD   time=HH:mm   adults=N   open=1   tonight=1
 */
import type { ActivityFilters } from "../constants/query-keys";

/** Categories whose venues carry opening hours, so day, time and "open" mean something. */
export const OPEN_HOURS_HUBS: ReadonlySet<string> = new Set(["restaurants", "spas", "nightclubs"]);

/** Where "Tables tonight" makes sense: you reserve a table there. */
export const TONIGHT_HUBS: ReadonlySet<string> = new Set(["restaurants", "nightclubs"]);

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export interface HubWhen {
  date?: string;
  time?: string;
  adults?: number;
  open: boolean;
  tonight: boolean;
}

/** The router JSON-encodes some values on client navigation; strip stray quotes. */
function str(raw: unknown): string | undefined {
  if (typeof raw === "number") return String(raw);
  return typeof raw === "string" ? raw.replace(/^"+|"+$/g, "") : undefined;
}

function flag(raw: unknown): boolean {
  const v = str(raw);
  return raw === true || v === "1" || v === "true";
}

/** Reads the five params. Anything malformed is dropped, never guessed. */
export function readHubWhen(search: Record<string, unknown>): HubWhen {
  const date = str(search.date);
  const time = str(search.time);
  const adultsRaw = Number(str(search.adults));
  return {
    ...(date && DATE_RE.test(date) ? { date } : {}),
    ...(time && TIME_RE.test(time) ? { time } : {}),
    ...(Number.isInteger(adultsRaw) && adultsRaw >= 1 && adultsRaw <= 50 ? { adults: adultsRaw } : {}),
    open: flag(search.open),
    tonight: flag(search.tonight),
  };
}

/** The API filters for the when-params, on the hubs that have opening hours only. */
export function hubWhenFilters(category: string | undefined, search: Record<string, unknown>): Partial<ActivityFilters> {
  if (!category || !OPEN_HOURS_HUBS.has(category)) return {};
  const when = readHubWhen(search);
  return {
    ...(when.date ? { date: when.date } : {}),
    ...(when.time ? { time: when.time } : {}),
    ...(when.open ? { open_now: "1" } : {}),
    ...(when.tonight && TONIGHT_HUBS.has(category) ? { tonight: "1" } : {}),
  };
}

/**
 * The search a bookable card's link carries so the detail page opens its
 * booking sheet already filled in: `?book=1&date=&time=&adults=`, exactly
 * what MaJourneys AI's proposal card sends. Undefined when the bar is empty,
 * so an ordinary click never pops the sheet open.
 */
export function bookingLinkSearch(
  when: Pick<HubWhen, "date" | "time" | "adults">,
): Record<string, string | number> | undefined {
  if (!when.date && !when.time && !when.adults) return undefined;
  // Numbers as numbers: the router writes a numeric string as "%221%22".
  return {
    book: 1,
    ...(when.date ? { date: when.date } : {}),
    ...(when.time ? { time: when.time } : {}),
    ...(when.adults ? { adults: when.adults } : {}),
  };
}

/**
 * The next search after one when-field changes. Starts from `prev`, so the
 * city, filters, sort and the other fields are kept; `undefined` drops a key.
 * Goes back to page 1 on purpose: a new day or time is a new list.
 */
export function patchSearch(prev: Record<string, unknown>, patch: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...prev };
  delete out.page;
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined || value === "" || value === false) delete out[key];
    else out[key] = value;
  }
  return out;
}

/** The filter params "Clear" empties. Never the city (a path segment), day, time or guests. */
export const CLEARABLE_FILTER_KEYS = ["tag", "cuisine", "open", "tonight", "min_price", "max_price", "sort", "show"] as const;

/** Clear in the Filters sheet: drops the filters only, keeps day, time, guests and everything else. */
export function clearFiltersSearch(prev: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...prev };
  for (const key of [...CLEARABLE_FILTER_KEYS, "page"]) delete out[key];
  return out;
}

/**
 * The Filters sheet's chip choices written into the search (#54): open and
 * tonight as the flag 1, a collection or cuisine as its id, an unset one removed. Only
 * the chip params move; day, time, guests and the rest stay as they were.
 */
export function applyChipsSearch(
  prev: Record<string, unknown>,
  chips: Record<string, string | undefined>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...prev };
  for (const [key, v] of Object.entries(chips)) {
    if (!v) delete out[key];
    else out[key] = key === "tag" || key === "cuisine" ? v : 1;
  }
  return out;
}

/** Removes one active filter (a chip's ×), keeping every other choice. */
export function removeFilterSearch(prev: Record<string, unknown>, keys: readonly string[]): Record<string, unknown> {
  const out: Record<string, unknown> = { ...prev };
  for (const key of [...keys, "page"]) delete out[key];
  return out;
}

/** Half-hour steps from `from` to `to` (a `to` past midnight wraps). */
function halfHours(from: string, to: string): string[] {
  const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const start = toMin(from);
  let end = toMin(to);
  if (end < start) end += 24 * 60;
  const out: string[] = [];
  for (let m = start; m <= end; m += 30) {
    const d = m % (24 * 60);
    out.push(`${String(Math.floor(d / 60)).padStart(2, "0")}:${String(d % 60).padStart(2, "0")}`);
  }
  return out;
}

/**
 * The times the bar offers on each hub. Every venue then applies its own rule
 * on the server (a restaurant seats every 30 minutes until an hour before it
 * closes; Noto only on the half hour), so a time here never promises a table.
 */
export function hubTimeOptions(category: string | undefined): string[] {
  if (category === "nightclubs") return halfHours("20:00", "03:00");
  if (category === "spas") return halfHours("09:00", "21:00");
  return halfHours("12:00", "00:00");
}

/** Up to this many guests in the bar's picker. */
export const MAX_BAR_GUESTS = 12;
